/**
 * The MCP tab on the Account screen (`mcp.token.manage.own`): the person's tokens as a list page
 * (expired ones flagged, revoked ones gone), with How to connect, Create token, Extend and Revoke.
 */

import { useState } from 'react';


import { useMcpTokens, useRevokeMcpToken } from '@/api/queries';
import type { McpToken } from '@/api/types';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { Pagination } from '@/ui/Pagination';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { sortRows } from '@/ui/sortRows';
import { useClientPaging } from '@/ui/useClientPaging';
import { useSort } from '@/ui/useSort';

import { ConnectDialog } from './ConnectDialog';
import { CreateTokenDialog } from './CreateTokenDialog';
import { ExtendTokenDialog } from './ExtendTokenDialog';
import { TokensTable } from './TokensTable';
import { ASCENDING_FIRST, matchesToken, NO_FILTERS, TOKEN_SORT } from './tokenSort';
import { TokensToolbar, type TokenFilters } from './TokensToolbar';

const PAGE_SIZE = 25;

function RevokeDialog({ token, onClose }: { token: McpToken; onClose: () => void }) {
  const revoke = useRevokeMcpToken();
  return (
    <ConfirmDialog
      title="Revoke this token?"
      confirmLabel="Revoke token"
      danger
      pending={revoke.isPending}
      error={revoke.error}
      onConfirm={() => revoke.mutate(token.id, { onSuccess: onClose })}
      onClose={onClose}
    >
      <p>Any AI tool using “{token.name}” stops working right away. This cannot be undone.</p>
    </ConfirmDialog>
  );
}

function TokensList({
  tokens,
  filters,
  onExtend,
  onRevoke,
}: {
  tokens: McpToken[];
  filters: TokenFilters;
  onExtend: (token: McpToken) => void;
  onRevoke: (token: McpToken) => void;
}) {
  // Newest first until a column is chosen.
  const { sort, order, onSort } = useSort('created', 'desc', ASCENDING_FIRST);
  const visible = sortRows(
    tokens.filter((token) => matchesToken(token, filters)),
    sort,
    order,
    TOKEN_SORT,
  );
  const paging = useClientPaging(visible, [filters, sort, order], PAGE_SIZE);
  const filtered = Boolean(filters.q || filters.status.length || filters.scope.length);
  return (
    <>
      <TokensTable
        tokens={paging.pageRows}
        empty={filtered ? 'No tokens match these filters.' : 'You have no tokens yet. Create a token, then use How to connect.'}
        sort={sort}
        order={order}
        onSort={onSort}
        onExtend={onExtend}
        onRevoke={onRevoke}
      />
      {visible.length > 0 && (
        <Pagination
          page={paging.page}
          pageSize={paging.pageSize}
          total={visible.length}
          noun={visible.length === 1 ? 'token' : 'tokens'}
          onPage={paging.setPage}
          onPageSize={paging.setPageSize}
        />
      )}
    </>
  );
}

export function TokensTab() {
  const tokens = useMcpTokens();
  const [filters, setFilters] = useState<TokenFilters>(NO_FILTERS);
  const [connecting, setConnecting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [extending, setExtending] = useState<McpToken | null>(null);
  const [revoking, setRevoking] = useState<McpToken | null>(null);

  return (
    <>
      <TokensToolbar filters={filters} onChange={(patch) => setFilters({ ...filters, ...patch })} onConnect={() => setConnecting(true)} onCreate={() => setCreating(true)} />
      {tokens.isPending && <LoadingCard label="Loading tokens" />}
      {!tokens.isPending && tokens.error && <LoadFailure error={tokens.error} onRetry={() => void tokens.refetch()} />}
      {tokens.data && <TokensList tokens={tokens.data.tokens} filters={filters} onExtend={setExtending} onRevoke={setRevoking} />}
      {connecting && <ConnectDialog onClose={() => setConnecting(false)} />}
      {creating && <CreateTokenDialog onClose={() => setCreating(false)} />}
      {extending && <ExtendTokenDialog token={extending} onClose={() => setExtending(null)} />}
      {revoking && <RevokeDialog token={revoking} onClose={() => setRevoking(null)} />}
    </>
  );
}
